import type { Capability, RoleRef, Section } from '@gsls/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip, inputClass } from './ui';
import { useSession } from '../lib/session';
import greensynLockup from '../assets/greensyn-logo-lockup.svg';

/**
 * Application shell: brand bar, sidebar navigation, the workspace picker, and the
 * signed-in identity.
 *
 * Two different things decide what a nav entry needs:
 *
 *  - `capability` — the API enforces it, and it is the union across *all* the
 *    roles the user holds. An entry is hidden when nobody granted the capability,
 *    which is a permission question.
 *  - `section` — which workspace the entry belongs to. This is presentation:
 *    the picker in the header changes it, and it never removes an action, only
 *    stops advertising it in the current view.
 */
interface NavItem {
  to: string;
  label: string;
  milestone: string;
  section: Section;
  capability?: Capability;
}

/**
 * The navigation, grouped. A flat list of thirteen entries is a wall of text;
 * the groups are the divisions someone actually thinks in — the master data you
 * maintain, the work you do, the numbers you read, and the things you
 * administer.
 */
interface NavGroup {
  /** Shown above the group. The first group is deliberately unlabelled: the top
   *  of a sidebar does not need a heading. */
  label?: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [{ to: '/', label: 'Dashboard', milestone: 'M0', section: 'dashboard' }],
  },
  {
    label: 'Master data',
    items: [
      { to: '/items', label: 'Items', milestone: 'M1', section: 'items' },
      { to: '/locations', label: 'Locations', milestone: 'M1', section: 'locations' },
      { to: '/suppliers', label: 'Suppliers', milestone: 'M1', section: 'suppliers' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/inventory', label: 'Inventory', milestone: 'M2', section: 'inventory' },
      { to: '/recipes', label: 'Recipes', milestone: 'M3', section: 'recipes' },
      { to: '/batches', label: 'Production batches', milestone: 'M4', section: 'batches' },
      { to: '/recovery', label: 'Recovery', milestone: 'M5', section: 'recovery' },
      { to: '/quality', label: 'Quality control', milestone: 'M5', section: 'quality' },
    ],
  },
  {
    label: 'Insight',
    items: [
      { to: '/reports', label: 'Reports', milestone: 'M6', section: 'reports' },
      {
        to: '/audit',
        label: 'Audit trail',
        milestone: 'M6',
        section: 'audit',
        capability: 'audit.read',
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        to: '/users',
        label: 'Users',
        milestone: 'M1',
        section: 'users',
        capability: 'users.manage',
      },
      {
        to: '/roles',
        label: 'Roles',
        milestone: 'M8',
        section: 'roles',
        capability: 'users.manage',
      },
    ],
  },
];

const SHIPPED = new Set(['M0', 'M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M8']);

/**
 * Workspace picker. Only shown when somebody holds more than one role — with one
 * role there is nothing to pick, and a control that does nothing is noise.
 */
function ViewPicker({
  roles,
  activeRole,
  onChange,
}: {
  roles: RoleRef[];
  activeRole: RoleRef | null;
  onChange: (roleId: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor="workspace-picker"
        className="text-[10px] font-bold uppercase tracking-widest text-ink-subtle"
      >
        View
      </label>
      <select
        id="workspace-picker"
        className={`${inputClass} w-auto py-1.5 text-xs`}
        value={activeRole?.id ?? ''}
        onChange={(event) => onChange(event.target.value)}
      >
        {roles.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
          </option>
        ))}
      </select>
      <Tooltip
        subject="this view"
        text="This only changes which screens the sidebar shows. You hold every one of your roles at once, so your permissions are identical whichever view you pick."
      />
    </div>
  );
}

/**
 * The signed-in identity, as a menu. Profile lives here rather than in the
 * sidebar because every account has one and it belongs to no workspace — putting
 * it behind a `section` would let a role configuration hide a person's own
 * account from them.
 */
function AccountMenu() {
  const { user, roles, activeRole, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Navigation closes it, so the menu never lingers over a new page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  if (!user) return null;

  const subtitle =
    roles.length > 1 ? user.email : `${activeRole?.name ?? 'No role'} · ${user.email}`;

  // A phone shows initials rather than the name, so the header stays one line.
  const initials =
    user.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || user.email.charAt(0).toUpperCase();

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${user.name}`}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center justify-center rounded-full border border-surface-border bg-white transition hover:border-brand sm:min-h-9 sm:justify-start sm:gap-2 sm:px-3 sm:py-1.5"
      >
        {/* Phone: initials. The name and subtitle return at sm and up. */}
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-soft text-xs font-bold uppercase tracking-wide text-brand-deep sm:hidden">
          {initials}
        </span>
        <span className="hidden sm:block">
          <span className="block whitespace-nowrap text-sm font-semibold leading-tight text-ink">{user.name}</span>
          <span className="hidden max-w-[16rem] truncate text-xs leading-tight text-ink-muted sm:block">
            {subtitle}
          </span>
        </span>
        <span aria-hidden="true" className="hidden text-[10px] text-ink-muted sm:inline">
          ▾
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-52 rounded-md border border-surface-muted bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/profile');
            }}
            className="block w-full px-4 py-2 text-left text-sm text-ink-body transition hover:bg-surface"
          >
            Profile
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            disabled={loggingOut}
            className="block w-full px-4 py-2 text-left text-sm text-ink-body transition hover:bg-surface disabled:opacity-50"
          >
            {loggingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { roles, activeRole, setActiveRole, can, shows } = useSession();
  const location = useLocation();
  /**
   * On a phone the sidebar becomes a single collapsible menu, so the dashboard
   * is not pushed below a wall of links. It closes itself on navigation.
   */
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  /**
   * Filter on both axes, then drop any group left empty. A heading with nothing
   * under it — or a separator with nothing on one side — would be worse than no
   * grouping at all: it draws the eye to something that is missing.
   */
  const visibleGroups = NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter(
      (item) => shows(item.section) && (!item.capability || can(item.capability)),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex min-h-full flex-col">
      <div className="gs-logo-line" />

      <header className="border-b border-surface-muted bg-white">
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-3 px-4 py-3 sm:gap-6 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3">
            {/* Height-driven, but sized so the whole lockup still fits a narrow
                phone: Tailwind's `img { max-width: 100% }` squashes the aspect
                ratio the moment the natural width exceeds the space. */}
            <img
              src={greensynLockup}
              alt="GreenSyn Life Science"
              className="h-8 w-auto shrink-0 sm:h-11"
            />
            <span aria-hidden="true" className="hidden h-9 w-px bg-surface-border sm:block" />
            <p className="hidden text-sm font-semibold uppercase tracking-wide text-brand-deep sm:block">
              Inventory &amp; Production
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 sm:gap-4">
            {/* The view picker needs room; below lg it moves down to the
                Sections menu rather than wrapping the header into ragged rows. */}
            {roles.length > 1 ? (
              <div className="hidden lg:block">
                <ViewPicker roles={roles} activeRole={activeRole} onChange={setActiveRole} />
              </div>
            ) : null}

            <AccountMenu />
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[1400px] flex-1 flex-col gap-6 px-4 py-4 sm:px-6 sm:py-6 lg:flex-row">
        <nav aria-label="Sections" className="lg:w-56 lg:shrink-0">
          {roles.length > 1 ? (
            <div className="mb-3 lg:hidden">
              <ViewPicker roles={roles} activeRole={activeRole} onChange={setActiveRole} />
            </div>
          ) : null}

          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 rounded-md border border-surface-border bg-white px-3 py-2 text-sm font-semibold text-ink-body transition hover:border-brand lg:hidden"
            aria-expanded={navOpen}
            aria-controls="app-nav-sections"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span>Sections</span>
            <span
              aria-hidden="true"
              className="text-xs font-bold uppercase tracking-wide text-ink-muted"
            >
              {navOpen ? 'Hide' : 'Show'}
            </span>
          </button>

          <div
            id="app-nav-sections"
            className={`${navOpen ? 'mt-3 block' : 'hidden'} lg:mt-0 lg:block`}
          >
            {/**
             * One list per group rather than one list with dividers wedged into it:
             * a divider is not a list item, and a group heading belongs to its
             * group. It also means the groups stack and wrap independently on a
             * narrow screen, so each starts on its own line instead of running on
             * from the previous one.
             */}
            {visibleGroups.map((group, index) => (
              <div key={group.label ?? group.items[0]?.to}>
                {index > 0 ? (
                  <div aria-hidden="true" className="my-3 h-px bg-surface-border" />
                ) : null}

                {group.label && index > 0 ? (
                  <p className="mb-1 hidden px-3 text-[10px] font-bold uppercase tracking-widest text-ink-subtle lg:block">
                    {group.label}
                  </p>
                ) : null}

                <ul className="flex flex-wrap gap-1 lg:flex-col">
                  {group.items.map((item) => {
                    const shipped = SHIPPED.has(item.milestone);
                    return (
                      <li key={item.to}>
                        <NavLink
                          to={shipped ? item.to : `/${item.to.replace('/', '')}-soon`}
                          end={item.to === '/'}
                          className={({ isActive }) =>
                            [
                              'flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition',
                              isActive
                                ? 'bg-brand-soft font-semibold text-brand-deep'
                                : 'text-ink-body hover:bg-surface',
                              shipped ? '' : 'opacity-60',
                            ].join(' ')
                          }
                        >
                          <span>{item.label}</span>
                          {shipped ? null : (
                            <span className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink-muted">
                              {item.milestone}
                            </span>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <main className="min-w-0 flex-1 pb-10">{children}</main>
      </div>

      <footer className="border-t border-surface-muted bg-white py-4">
        <div className="mx-auto w-full max-w-[1400px] px-6 text-xs text-ink-muted">
          GreenSyn Life Science — Sustainable healthcare for all
        </div>
      </footer>
    </div>
  );
}
